
import { useNavigate } from 'react-router-dom';

interface ConfirmationModalProps {
  isOpen: boolean;
  bookingDetails: {
    date: string;
    time: string;
    name: string;
    email: string;
    amount: string;
    currency: string;
    txRef: string;
  } | null;
}

export default function ConfirmationModal({
  isOpen,
  bookingDetails,
}: ConfirmationModalProps) {
  const navigate = useNavigate();

  // Guard clause – render nothing if modal is closed or data missing
  if (!isOpen || !bookingDetails) return null;

  // Helper to safely format time strings (e.g., "14:30")
  const formatTime = (time: string) => {
    const [hours = '0', minutes = '00'] = time.split(':');
    const h = parseInt(hours, 10);
    if (Number.isNaN(h)) return time; // fallback if parsing fails
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayH = h % 12 || 12;
    return `${displayH}:${minutes} ${ampm}`;
  };

  // Helper to safely format dates – protects against invalid ISO strings
  const formatDate = (dateStr: string) => {
    try {
      const date = new Date(dateStr + 'T00:00:00');
      if (Number.isNaN(date.getTime())) throw new Error('Invalid date');
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      });
    } catch {
      return dateStr; // fallback to raw string if formatting fails
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl max-w-md w-full p-8 text-center space-y-6 animate-[scaleIn_0.3s_ease-out]">
        <div className="w-20 h-20 mx-auto bg-emerald-100 rounded-full flex items-center justify-center">
          <i className="ri-check-line text-4xl text-emerald-600"></i>
        </div>

        <div>
          <h3 className="text-2xl font-bold text-slate-900 mb-2">
            Booking Confirmed!
          </h3>
          <p className="text-sm text-gray-500">
            Your consultation has been successfully booked. A confirmation email
            has been sent to{' '}
            <strong className="text-slate-700">{bookingDetails.email}</strong>.
          </p>
        </div>

        <div className="bg-gray-50 rounded-2xl p-5 space-y-3 text-left">
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Date</span>
            <span className="text-sm font-medium text-slate-900">
              {formatDate(bookingDetails.date)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Time</span>
            <span className="text-sm font-medium text-slate-900">
              {formatTime(bookingDetails.time)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Amount Paid</span>
            <span className="text-sm font-medium text-emerald-600">
              {bookingDetails.amount} {bookingDetails.currency}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-gray-500">Reference</span>
            <span className="text-xs font-mono text-gray-600">
              {bookingDetails.txRef}
            </span>
          </div>
        </div>

        <button
          onClick={() => navigate('/')}
          className="w-full py-4 bg-slate-900 text-white rounded-xl text-sm font-semibold hover:bg-slate-800 transition-all whitespace-nowrap cursor-pointer"
        >
          Back to Home
        </button>
      </div>
    </div>
  );
}
